import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Abas, PainelAba } from '../../componentes/abas';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Indicador } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { dataHora, mesAno, numero } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';

export type SerieIndice = 'igpm' | 'ipca' | 'inpc' | 'selic';

export interface PontoIndice {
  competencia: string;
  valor: number;
  acumulado_12m: number | null;
}

export interface HistoricoIndiceApi {
  serie: SerieIndice;
  codigo: number;
  atualizado_em: string;
  /** Do mês mais novo para o mais antigo. */
  pontos: PontoIndice[];
}

/** Chave da consulta do histórico (relida depois do "Atualizar índices agora"). */
export const CHAVE_HISTORICO_INDICES = ['financeiro', 'indices', 'historico'] as const;
/** Tempo até reler os índices depois do pedido (a sincronização roda no worker, em segundos). */
export const RELER_INDICES_MS = 8_000;
/** Meses do histórico na tabela. */
export const MESES_HISTORICO = 24;

const pct = (v: number) => `${numero(v, 2)}%`;

/** Atualização mais recente entre as séries (o "Atualizado em" da fonte). Função pura, testada em `tests/w31-navegacao.test.ts`. */
export function ultimaAtualizacao(series: Pick<HistoricoIndiceApi, 'atualizado_em'>[]): string | null {
  return series.reduce<string | null>((m, s) => (m === null || s.atualizado_em > m ? s.atualizado_em : m), null);
}

/**
 * Fase 2 (W21, K7; W31: saiu da barra superior para a página): "Atualizar índices agora", só para quem administra os
 * conectores (Dono e Administrador pelas regras de fábrica). Enfileira a sincronização do Banco Central
 * (`POST /integracoes/indices/atualizar`, um pedido a cada 10 minutos por workspace) e relê os índices em seguida.
 */
function AtualizarIndicesAgora() {
  const { t } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => post<{ job_id: string }>('/integracoes/indices/atualizar', {}),
    onSuccess: () => {
      toast.sucesso(t('barra.atualizacaoPedida'));
      setTimeout(() => void qc.invalidateQueries({ queryKey: CHAVE_HISTORICO_INDICES }), RELER_INDICES_MS);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  if (!s.instalado('integracoes') || !s.pode('integracoes.conectores')) return null;
  return (
    <AcaoPrimaria>
      <Botao data-atualizar-indices="" ocupado={m.isPending} onClick={() => m.mutate()} icone={<RefreshCw className={`size-4 ${m.isPending ? 'animate-spin motion-reduce:animate-none' : ''}`} aria-hidden />}>
        {t('barra.atualizarAgora')}
      </Botao>
    </AcaoPrimaria>
  );
}

const TH = 'px-4 py-2.5 font-medium whitespace-nowrap';
const TD = 'px-4 py-2.5';

/** Tabela do histórico de um índice: mês, variação no mês e acumulado de 12 meses, do mais novo para o mais antigo. */
function TabelaHistorico({ serie, nome }: { serie: HistoricoIndiceApi; nome: string }) {
  const { t } = useTranslation('financeiro');
  return (
    <div tabIndex={0} role="region" aria-label={t('indices.tabela', { indice: nome })} className="relative overflow-x-auto focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foco">
      <table className="w-full text-sm" data-tabela-indice={serie.serie}>
        <caption className="sr-only">{t('indices.tabela', { indice: nome })}</caption>
        <thead>
          <tr className="border-b border-borda text-left text-tinta-3">
            <th scope="col" className={TH}>
              {t('indices.mes')}
            </th>
            <th scope="col" className={`${TH} text-right`}>
              {t('indices.noMes')}
            </th>
            <th scope="col" className={`${TH} text-right`}>
              {t('indices.acumulado')}
            </th>
          </tr>
        </thead>
        <tbody>
          {serie.pontos.map((p) => (
            <tr key={p.competencia} className="border-b border-borda last:border-0">
              <th scope="row" className={`${TD} text-left font-normal whitespace-nowrap`}>
                {mesAno(p.competencia)}
              </th>
              <td className={`${TD} numero text-right whitespace-nowrap`}>{pct(p.valor)}</td>
              <td className={`${TD} text-right whitespace-nowrap ${p.acumulado_12m === null ? 'text-tinta-3' : 'numero'}`}>{p.acumulado_12m === null ? t('indices.semAcumulado') : pct(p.acumulado_12m)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Fase 2 (W31, item 2 do Gustavo): Financeiro > Índices. A faixa da barra superior virou página: IGP-M, IPCA, INPC e SELIC com
 * a variação do mês mais recente e o acumulado de 12 meses (um `Indicador` por índice) e o histórico mensal em tabela (um
 * índice por vez, nas `Abas` do núcleo). O projeto não tem biblioteca de gráfico e o gráfico do Início é de fluxo de caixa,
 * então a página fica nos números. Fonte: `GET /inicio/indices/historico` (dados globais do Banco Central, sincronizados pelo
 * worker); quem administra os conectores pede a atualização aqui.
 */
export function Indices() {
  const { t } = useTranslation('financeiro');
  const { t: tn } = useTranslation();
  const q = useQuery({
    queryKey: CHAVE_HISTORICO_INDICES,
    queryFn: () => get<HistoricoIndiceApi[]>('/inicio/indices/historico', { meses: MESES_HISTORICO }),
    staleTime: 3_600_000,
  });
  const [escolhida, setEscolhida] = useState<SerieIndice>('igpm');
  const nome = (s: SerieIndice) => tn(`barra.nomes.${s}`);
  if (q.isPending) return <Carregando />;
  if (q.isError) return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  const series = q.data;
  const atual = series.find((s) => s.serie === escolhida) ?? series[0];
  const atualizado = ultimaAtualizacao(series);
  return (
    <div className="flex min-w-0 flex-col gap-5">
      <AtualizarIndicesAgora />
      {series.length === 0 || !atual ? (
        <Vazio texto={t('indices.vazio')} />
      ) : (
        <>
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label={tn('barra.indices')}>
            {series.map((s) => {
              const ultimo = s.pontos[0];
              return (
                <li key={s.serie} data-indice={s.serie} className="min-w-0">
                  <Indicador
                    rotulo={nome(s.serie)}
                    valor={ultimo ? pct(ultimo.valor) : ''}
                    detalhe={
                      ultimo && (
                        <>
                          <span className="block">{t('indices.competencia', { mes: mesAno(ultimo.competencia) })}</span>
                          <span className="block">{ultimo.acumulado_12m === null ? t('indices.semAcumulado') : t('indices.acumuladoValor', { valor: pct(ultimo.acumulado_12m) })}</span>
                        </>
                      )
                    }
                  />
                </li>
              );
            })}
          </ul>
          <Cartao titulo={t('indices.historico')} semPadding>
            <div className="flex flex-col gap-3 pt-3">
              <Abas<SerieIndice>
                className="mx-4"
                idBase="historico-indices"
                rotulo={t('indices.serie')}
                ativa={atual.serie}
                aoMudar={setEscolhida}
                itens={series.map((s) => ({ chave: s.serie, rotulo: nome(s.serie) }))}
              />
              <PainelAba idBase="historico-indices" chave={atual.serie}>
                <TabelaHistorico serie={atual} nome={nome(atual.serie)} />
              </PainelAba>
            </div>
          </Cartao>
          {atualizado && <p className="text-xs text-tinta-3">{t('indices.fonte', { data: dataHora(atualizado) })}</p>}
        </>
      )}
    </div>
  );
}
